'use client';

import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfilePublicRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { RollingAction } from '@diritto/common/rolling/constants';
import { RollingReactionDto } from '@diritto/common/rolling/dtos/rolling-reaction.dto';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackerExtraInfo, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { RollingMoreMenu } from 'app-components/rolling/RollingMoreMenu';
import { RollingReactions } from 'app-components/rolling/RollingReactions';
import { RollingViewerContentBanner } from 'app-components/rolling/RollingViewerContentBanner';
import { ButtonSize, ButtonType } from 'constants/button';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { usePathname } from 'next/navigation';
import { forwardRef, Ref, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { RollingDispatchParams } from 'state-hooks/rolling/useRollingDispatch';
import { getTextLineStyle } from 'styles/common';
import { Button } from 'ui-components/common/Button';
import { Link } from 'ui-components/common/Link';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { hasScroll, isInViewport } from 'utils/common';

type RollingViewerItemProps = {
  rolling: RollingRelayDto;
  rollingReaction?: RollingReactionDto;
  profile?: ProfilePublicRelayDto;
  content?: ContentPublicRelayDto;
  contentAuthorProfile?: ProfilePublicRelayDto;
  onDispatchAction?: (params: RollingDispatchParams) => void;
  showMoreMenu?: boolean;
  trackerExtraInfo: TrackerExtraInfo;
};
export type RollingViewerItemRef = { rollingId: number; onScroll: () => void };

const getActions = ({ myProfileId, rollingProfileId }: { myProfileId?: number; rollingProfileId: number }) => {
  // 로그인 안한 상태
  if (!myProfileId) {
    return [];
  }

  return rollingProfileId === myProfileId ? [RollingAction.GoToEdit, RollingAction.Delete] : [];
};

export const RollingViewerItem = forwardRef(
  (
    {
      rolling,
      rollingReaction,
      profile,
      content,
      contentAuthorProfile,
      trackerExtraInfo,
      onDispatchAction,
      showMoreMenu,
    }: RollingViewerItemProps,
    ref: Ref<RollingViewerItemRef>,
  ) => {
    const { tracker } = useTrackingState();
    const pathname = usePathname() ?? '';
    const localRef = useRef<HTMLDivElement | null>(null);
    const [fold, setFold] = useState(true);

    const { profile: myProfile } = useAuthState();
    const actions = useMemo(
      () => getActions({ rollingProfileId: rolling.profileId, myProfileId: myProfile?.id }),
      [myProfile?.id, rolling.profileId],
    );

    const handleClickToProfile = useCallback(() => {
      if (profile) {
        tracker.track(TrackingEventType.ClickToProfileDetail, { profileId: profile.id, trackerExtraInfo });
      }
    }, [profile, tracker, trackerExtraInfo]);

    const handleScroll = useCallback(() => {
      if (!localRef.current) {
        return;
      }

      if (isInViewport(localRef.current)) {
        if (!pathname.startsWith(`/rollings/${rolling.id}`)) {
          window.history.replaceState({}, '', `/rollings/${rolling.id}`);
          tracker.track(TrackingEventType.ViewRolling, { rollingId: rolling.id });
        }
      } else {
        if (localRef.current?.scrollTop !== 0) {
          localRef.current?.scrollTo(0, 0);
        }

        if (hasScroll(localRef.current)) {
          setFold(true);
        }
      }
    }, [pathname, rolling.id, tracker]);

    const handleMoreButtonClick = useCallback(() => {
      setFold(false);
      tracker.track(TrackingEventType.ClickRollingReadMore, { rollingId: rolling.id });
    }, [rolling.id, tracker]);

    useImperativeHandle(ref, () => ({
      onScroll: handleScroll,
      rollingId: rolling.id,
    }));

    const handleRef = useCallback((node: HTMLDivElement | null) => {
      if (node) {
        localRef.current = node;

        // UI 가 로드되고 스크롤이 없으면 접는 UI 제거한다.
        if (!hasScroll(localRef.current)) {
          setFold(false);
        }
      }
    }, []);

    if (!profile || !rolling) {
      return null;
    }

    return (
      <StyledContainer ref={handleRef} fold={fold}>
        <StyledHeadWrapper>
          <StyledProfileMetaWrapper>
            <Link href={`/accounts/profiles/${profile.id}`} onClick={handleClickToProfile}>
              <ProfileImage file={profile.profileImage} defaultImageKey={profile.nickname} size={24} />
            </Link>
            <StyledName>
              <Link href={`/accounts/profiles/${profile.id}`} onClick={handleClickToProfile}>
                {profile.nickname}
              </Link>
            </StyledName>
            {rolling && <StyledDate date={rolling?.createdAt} />}
            {showMoreMenu && actions.length > 0 && onDispatchAction && (
              <RollingMoreMenu rolling={rolling} onDispatchAction={onDispatchAction} actions={actions} />
            )}
          </StyledProfileMetaWrapper>
          <StyledTitle>{rolling?.title ?? ''}</StyledTitle>
        </StyledHeadWrapper>
        <StyledDescription>{rolling?.description ?? ''}</StyledDescription>
        <RollingReactions rolling={rolling} rollingReaction={rollingReaction} />

        {content && contentAuthorProfile && (
          <StyledRollingViewerContentBanner content={content} profile={contentAuthorProfile} />
        )}

        {fold && (
          <StyledMoreButtonWrapper>
            <StyledMoreButton
              size={ButtonSize.Medium}
              buttonType={ButtonType.NaturalNoBorder}
              buttonTypography={Typography.XSmall}
              fontWeight={FontWeight.Medium}
              fullWidth
              label="계속 읽기"
              onClick={handleMoreButtonClick}
            />
          </StyledMoreButtonWrapper>
        )}
      </StyledContainer>
    );
  },
);

RollingViewerItem.displayName = 'RollingViewerItem';

const StyledRollingViewerContentBanner = styled(RollingViewerContentBanner)`
  margin-top: 24px;
`;

const StyledContainer = styled.div(
  ({ fold }: { fold: boolean }) => css`
    position: relative;
    padding: 16px;

    width: 100%;
    height: 100%;
    max-height: 100%;
    overflow-y: ${fold ? 'hidden' : 'scroll'};

    scroll-snap-align: start;
    scroll-snap-stop: always;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  `,
);

const StyledHeadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  margin-bottom: 16px;
`;

const StyledProfileMetaWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledName = styled.div`
  display: flex;
  align-items: center;
  color: ${Color.Gray700};
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};

  & > a {
    margin-right: 4px;
    width: fit-content;
    ${getTextLineStyle(1)};
  }
`;

const StyledDate = styled(RelativeDateText)`
  flex-shrink: 0;
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray500};
`;

const StyledTitle = styled.h1`
  margin: 0;
  ${Typography.XLarge};
  font-weight: ${FontWeight.Bold};
`;

const StyledDescription = styled.p`
  padding: 10px 0;

  white-space: pre-wrap;

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;

const StyledMoreButtonWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;

  width: 100%;
  height: 200px;
  padding: 16px;

  display: flex;
  justify-content: center;
  align-items: flex-end;

  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 61.46%);
`;

const StyledMoreButton = styled(Button)`
  bottom: 0;
`;
